import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { CheckCircle2Icon, XCircleIcon } from 'lucide-react';

import { ApiError, api, type ResponseDto } from '@/lib/api';
import { notify } from '@/lib/notify';
// Aliased: `t` in this file is the CONSOLE-string translator, and the content
// resolver is `contentIn(text, locale)` — an explicit two-argument call, so a
// reader can see at a glance which of the two languages any given line follows.
import { directionOf, secondary, t as contentIn } from '@/lib/locale';
import { useLabel, useT } from '@/lib/i18n';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  RENDERERS, UnsupportedItem, isAnswered,
  type DeliveryItem, type ResponseValue,
} from '@/features/run/renderers';

/**
 * The take-test harness (build/05 §F, build/07 Phase 6).
 *
 * AN INTERNAL QA TOOL, not the candidate experience — that belongs to the NAFIS
 * consumer system (PRD §2.2). Its job is to exercise the real pipeline through
 * the real API so the whole chain can be seen working.
 *
 * It renders every item through `RENDERERS[item.itemType]` and contains no
 * branch on model or on type. Switching module changes the item types, the
 * plugin that scores them and the result visualisation, and the only code that
 * knows is those two lookup maps — that is the Phase-6 architecture check.
 *
 * WHAT IT NEVER SEES. The payload is key-stripped by the server, so there is no
 * `dimension`, `isCorrect` or `reverseKeyed` to leak into the DOM even by
 * accident. This screen could not reveal a scoring key if it tried.
 */

type Answers = Record<string, ResponseValue>;

export function RunAssessment() {
  const { assessmentId = '' } = useParams();
  const navigate = useNavigate();

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [reviewing, setReviewing] = useState(false);
  const [missing, setMissing] = useState<string[]>([]);
  const displayedAt = useRef<number>(Date.now());

  const delivery = useQuery({
    queryKey: ['assessment-items', assessmentId],
    queryFn: () => api.assessmentItems(assessmentId),
    enabled: Boolean(assessmentId),
    // The saved answers come back on this payload; refetching mid-run would
    // overwrite what the candidate is typing.
    staleTime: Infinity,
  });

  /**
   * The instance, for `administeredLocale` — which the ITEMS payload does not
   * carry, deliberately.
   *
   * Contract v0's `DeliveryPayload` is the items and nothing else; the
   * administration language belongs to the *instance*, which a consumer already
   * holds because `startAssessment` returned it. So this harness makes the same
   * two calls a consumer makes rather than asking the server to bend the payload
   * for the console's convenience.
   *
   * Pinned at generation and never renegotiated: `staleTime: Infinity` because
   * this value CANNOT change for the life of the run. Changing administration
   * language mid-assessment voids the run's psychometric validity, which is why
   * the field is pinned in the first place (`08 §1.6`).
   */
  const instance = useQuery({
    queryKey: ['assessment', assessmentId],
    queryFn: () => api.assessment(assessmentId),
    enabled: Boolean(assessmentId),
    staleTime: Infinity,
  });

  /**
   * Falls back to English rather than blocking on the instance query.
   *
   * The items usually arrive first, and a harness that renders nothing until a
   * second request lands would be slower for no gain. `en-AE` is the wrong
   * guess for an Arabic run — but only for the moment before the instance
   * resolves, and `t()` never renders blank either way.
   */
  const locale = instance.data?.administeredLocale ?? 'en-AE';

  /**
   * TWO TRANSLATORS, AND THE DIFFERENCE BETWEEN THEM IS THE POINT (H1).
   *
   * `tRun` is bound to the run's **administered locale**; `t` follows the
   * **operator's profile**. Every string a candidate reads while answering — the
   * question counter, Back, Next, Review, Submit, "unanswered" — goes through
   * `tRun`, because it is part of the administration and an operator whose
   * profile is English must not silently re-administer an Arabic run in English.
   * Strings that speak to the OPERATOR about the run — the harness disclaimer,
   * the badge naming the administration language, load failures — go through
   * `t`.
   *
   * If these ever collapse into one, it will be `tRun` that disappears, and the
   * bug will be invisible until somebody compares two runs' envelopes. There is
   * a test in `administered-locale.test.tsx` guarding exactly that.
   */
  const t = useT();
  const tRun = useT(locale);
  const label = useLabel();

  const items = useMemo<DeliveryItem[]>(() => delivery.data?.items ?? [], [delivery.data]);

  // RESUME: seed local state from the answers the server already holds.
  useEffect(() => {
    if (!delivery.data) return;
    const restored: Answers = {};
    for (const item of delivery.data.items) {
      if (item.savedResponse !== null && item.savedResponse !== undefined) {
        restored[item.itemId] = item.savedResponse;
      }
    }
    setAnswers(restored);
    // Continue from the first unanswered item rather than the top — returning
    // to question 1 of 12 after answering 11 is its own small punishment.
    const firstUnanswered = delivery.data.items.findIndex(
      (i) => i.savedResponse === null || i.savedResponse === undefined,
    );
    setIndex(firstUnanswered === -1 ? 0 : firstUnanswered);
  }, [delivery.data]);

  useEffect(() => {
    displayedAt.current = Date.now();
  }, [index]);

  const save = useMutation({
    mutationFn: (responses: ResponseDto[]) => api.saveResponses(assessmentId, responses),
  });

  const submit = useMutation({
    mutationFn: (responses: ResponseDto[]) => api.submitAssessment(assessmentId, responses),
    onSuccess: (result) => {
      notify.success(t('run.submitted'), t('run.submitted.detail', { result: result.resultId }));
      navigate(`/results/${result.resultId}`);
    },
    onError: (err) => {
      // The server names the unanswered items; surface them in Review rather
      // than as an opaque "incomplete".
      const details = err instanceof ApiError
        ? (err.payload.details as { missingItemIds?: string[] } | undefined)
        : undefined;
      if (details?.missingItemIds?.length) {
        setMissing(details.missingItemIds);
        setReviewing(true);
        notify.error(
          t('run.incomplete'),
          t('run.incomplete.detail', { count: details.missingItemIds.length }),
        );
        return;
      }
      notify.error(
        t('run.submitFailed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      );
    },
  });

  const current = items[index];
  const answeredCount = items.filter((i) => isAnswered(i, answers[i.itemId])).length;
  const progress = items.length === 0 ? 0 : Math.round((answeredCount / items.length) * 100);
  const complete = items.length > 0 && answeredCount === items.length;

  const toResponses = (): ResponseDto[] =>
    items
      .filter((i) => isAnswered(i, answers[i.itemId]))
      .map((i) => ({ itemId: i.itemId, value: answers[i.itemId]! }));

  /** Autosave on change — this is what makes save-and-exit work at all. */
  function answer(item: DeliveryItem, value: ResponseValue) {
    setAnswers((prev) => ({ ...prev, [item.itemId]: value }));
    setMissing((prev) => prev.filter((id) => id !== item.itemId));
    save.mutate([
      {
        itemId: item.itemId,
        value,
        // QC only. Never touches a score — it feeds the `speeding` flag.
        responseTimeMs: Math.max(0, Date.now() - displayedAt.current),
      },
    ]);
  }

  if (delivery.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (delivery.error) {
    return (
      <div className="mx-auto max-w-2xl">
        <EmptyState
          icon={XCircleIcon}
          title={t('run.loadFailed')}
          description={
            delivery.error instanceof ApiError && delivery.error.status === 410
              ? t('run.expired')
              : delivery.error instanceof Error
                ? delivery.error.message
                : t('table.errorDescription')
          }
        />
      </div>
    );
  }

  /* ------------------------------------------------------------- review */

  if (reviewing) {
    return (
      <div className="mx-auto max-w-2xl">
        {/* The review list is the candidate's, so it is `tRun` throughout. */}
        <PageHeader
          eyebrow={t('run.eyebrow')}
          title={tRun('run.review.title')}
          description={tRun('run.review.description')}
          actions={
            <Badge variant={complete ? 'default' : 'destructive'}>
              {tRun('run.answeredOf', { answered: answeredCount, total: items.length })}
            </Badge>
          }
        />

        <div className="mb-4 space-y-2">
          {items.map((item, i) => {
            const done = isAnswered(item, answers[item.itemId]);
            const flagged = missing.includes(item.itemId);
            return (
              <button
                key={item.itemId}
                type="button"
                onClick={() => { setIndex(i); setReviewing(false); }}
                className={`hover:bg-muted/50 flex w-full items-center gap-3 rounded-md border p-3 text-start ${
                  flagged ? 'border-destructive' : ''
                }`}
              >
                <span className="text-muted-foreground w-6 text-sm">{i + 1}</span>
                <span className="flex-1" dir={directionOf(locale)}>{contentIn(item.prompt, locale)}</span>
                {done ? (
                  <CheckCircle2Icon className="size-4 shrink-0" />
                ) : (
                  <Badge variant="destructive">{tRun('run.unanswered')}</Badge>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setReviewing(false)}>
            {tRun('run.keepAnswering')}
          </Button>
          <Button
            onClick={() => submit.mutate(toResponses())}
            // Disabled until complete — the server would reject it anyway, and
            // a button that submits into a 422 is a button that lies.
            disabled={!complete || submit.isPending}
          >
            {submit.isPending ? tRun('run.scoring') : tRun('run.submit')}
          </Button>
        </div>
      </div>
    );
  }

  /* ---------------------------------------------------------------- take */

  if (!current) {
    return (
      <div className="mx-auto max-w-2xl">
        <EmptyState
          icon={XCircleIcon}
          title={t('run.noItems')}
          description={t('run.noItems.description')}
        />
      </div>
    );
  }

  const Renderer = RENDERERS[current.itemType];

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader
        eyebrow={t('run.eyebrow')}
        // `tRun`: the counter is what the CANDIDATE reads to know where they are.
        title={tRun('run.questionOf', { n: index + 1, total: items.length })}
        // `t`: this line is about the harness, addressed to the operator.
        description={t('run.description')}
        actions={
          <div className="flex items-center gap-2">
            {/*
              WHICH LANGUAGE THIS RUN IS BEING ADMINISTERED IN, which an operator
              had no way to see before — the harness rendered English first
              regardless, so an Arabic-first persona looked identical to an
              English one. It is stamped on the result envelope as reproducibility
              metadata, so it is a fact about the run and not a display setting.
            */}
            <Badge variant="secondary">
              {/* The language NAMED IN THE OPERATOR'S language, not in itself:
                  this badge tells an operator a fact about the run, so it reads
                  "Administered in Arabic". The picker in Profile does the
                  opposite, and for the opposite reason. */}
              {t('run.administeredIn', { language: label('languageName', locale) })}
            </Badge>
            {save.isPending && <Badge variant="outline">{t('common.saving')}</Badge>}
          </div>
        }
      />

      <Progress value={progress} className="mb-6" aria-label={tRun('run.percentAnswered', { progress })} />

      <Card>
        <CardHeader>
          {/*
            THE PROMPT IN THE LANGUAGE THIS RUN IS ADMINISTERED IN, with the
            other beneath it — not English first, always, whatever the instance
            was pinned to. `dir` is per line, because direction belongs to a
            language rather than to the page.
          */}
          <CardTitle className="text-lg" dir={directionOf(locale)}>
            {contentIn(current.prompt, locale)}
          </CardTitle>
          {secondary(current.prompt, locale) && (
            <CardDescription
              dir={directionOf(locale) === 'rtl' ? 'ltr' : 'rtl'}
              className="text-start text-base"
            >
              {secondary(current.prompt, locale)}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {/* THE ONE LOOKUP. No branch on type or model anywhere else. */}
          {Renderer ? (
            <Renderer
              item={current}
              value={answers[current.itemId] ?? null}
              onChange={(value) => answer(current, value)}
              locale={locale}
            />
          ) : (
            <UnsupportedItem item={current} />
          )}
        </CardContent>
      </Card>

      <div className="mt-6 flex items-center justify-between gap-2">
        <Button variant="outline" disabled={index === 0} onClick={() => setIndex((i) => i - 1)}>
          {tRun('run.back')}
        </Button>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={() => setReviewing(true)}>
            {tRun('run.reviewAction')}
          </Button>
          {index < items.length - 1 ? (
            <Button
              onClick={() => setIndex((i) => i + 1)}
              disabled={!isAnswered(current, answers[current.itemId])}
            >
              {tRun('run.next')}
            </Button>
          ) : (
            <Button onClick={() => setReviewing(true)}>{tRun('run.finish')}</Button>
          )}
        </div>
      </div>

      <p className="text-muted-foreground mt-4 text-sm">{tRun('run.autosave')}</p>
    </div>
  );
}
